import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const signup = async (email, password, displayName, role = 'user', publicUsername = null, extraFields = {}) => {
    const data = await api.signup(email, password, displayName, role, publicUsername, extraFields);
    setCurrentUser(data.user);
    setUserProfile(data.user);
    return data.user;
  };

  const updateProfile = async (profileData) => {
    const updatedUser = await api.updateProfile(profileData);
    setCurrentUser(updatedUser);
    setUserProfile(updatedUser);
    return updatedUser;
  };

  const uploadProfilePhoto = async (file) => {
    const updatedUser = await api.uploadProfilePhoto(file);
    setCurrentUser(updatedUser);
    setUserProfile(updatedUser);
    return updatedUser;
  };

  const login = async (email, password) => {
    const data = await api.login(email, password);
    setCurrentUser(data.user);
    setUserProfile(data.user);
    return data.user;
  };

  const logout = () => {
    api.logout();
    setCurrentUser(null);
    setUserProfile(null);
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await api.getCurrentUser();
        if (user) {
          setCurrentUser(user);
          setUserProfile(user);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    updateProfile,
    uploadProfilePhoto,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
